import {BankingInfo} from './banking-info';
import {Domain} from './domain';
import {FeeSnapshot} from './fee';
import {PaymentProviderResponse} from './payment';
import {RecipientBankAccount} from './recipient-bank-account';
import {Recipient} from './recipient';
import {UserContext} from './user';
import {Transaction} from './transaction';
import {StatusHistory} from './system';
import {WalletRef} from './wallet';

export interface Withdrawal {
  id: number;
  amount: number;
  status: WithdrawalStatus;
  recipient: Recipient;
  domain: Domain;
  wallet?: WalletRef;
  transactions: Transaction[];
  feeSnapshot: FeeSnapshot;
  internalNotes?: string;
  conciliatedAt?: Date;
  createdAt: Date;
  updatedAt: Date;

  statusHistory?: StatusHistory[];

  calculatedFee: number;

  paymentMetadata: PaymentProviderResponse<Object, Object>;

  receiverPaymentInfo?: WithdrawalReceiverPaymentInfo | null;

  bankAccount?: RecipientBankAccount | null;

  userContext?: UserContext;

  /** Optional API-only fields */
  identifier?: string;
  description?: string;
  finalAmount?: number | null;
  payoutPolicy?: string;
  externalId?: string;
  webhookUrl?: string;
}

export enum WithdrawalStatus {
  CANCELED = 'canceled',
  FAILED = 'failed',
  PROCESSING = 'processing',
  SCHEDULED = 'scheduled',
  SUCCESS = 'success',
  PENDING = 'pending',
  PENDING_APPROVAL = 'pending_approval',
  REJECTED = 'rejected',
  REFUNDED = 'refunded',
}

/** Manual refund reason keys for domain offline status updates (SUCCESS → REFUNDED). */
export enum WithdrawalRefundReason {
  CUSTOMER_REFUND = 'customerRefund',
  WRONG_DESTINATION = 'wrongDestination',
  DUPLICATE_PAYMENT = 'duplicatePayment',
  OTHER = 'other',
}

/** Status values used in withdrawal `statusHistory` for processing / error events (not the main withdrawal lifecycle enum). */
export enum WithdrawalStatusHistoryStatus {
  RETRY_SCHEDULED = 'retry_scheduled',
  PROCESSING_ERROR = 'processing_error',
  INSUFFICIENT_BALANCE = 'insufficient_balance',
  WITHDRAWAL_LIMIT_BLOCKED = 'withdrawal_limit_blocked',
  RECIPIENT_NOT_FOUND = 'recipient_not_found',
  BANK_ACCOUNT_INVALID = 'bank_account_invalid',
  DISPATCH_ERROR = 'dispatch_error',
  PROVIDER_FAILURE = 'provider_failure',
}

export interface WithdrawalPreview {
  bankingInfo: BankingInfo;
  amount: number;
  calculatedFee: number;
  withdrawalLimit?: number;

  transactions: Transaction[];
}
export interface WithdrawalReceiverPaymentInfo {
  pixKey?: string;
  /** PIX key type (from RecipientBankAccount enum or RecipientBankingInfo string union). */
  pixKeyType?: string;
  holderName?: string;
  holderDocument?: string;
  /** When destination was provided as EMV (copia-e-cola). */
  emvCode?: string;
  /** Name/document from request (EMV or inline bankAccount). */
  name?: string;
  document?: string;
  /** Digitable line when payout was a bank-slip bill payment. */
  digitableLine?: string;
}

/** Response from `POST /withdrawals/.../bank-slip/validate`. */
export interface BankSlipValidateResponse {
  digitableLine: string;
  expiresDate?: string;
  /** Face value in BRL (2 decimals). */
  amount: number;
  issuingEntity?: {code?: string; name?: string};
  finalBeneficiary?: {name?: string; taxID?: string};
}

/** Payload for `POST /withdrawals/.../bank-slip` (amount resolved from boleto). */
export interface BankSlipPayPayload {
  digitableLine: string;
  walletId: number;
  webhookUrl?: string;
  externalId?: string;
  description?: string;
}
